import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  usePublishSurvey,
  useSaveSurveyDraft,
  useSurvey,
  useUpdateSurvey,
} from '../../api/surveys.api';
import { checkSurveyReadiness, useSurveyBuilderStore } from '../../features/surveys/builder/store';
import { FieldPalette } from '../../features/surveys/builder/FieldPalette';
import { PageRail } from '../../features/surveys/builder/PageRail';
import { PageCanvas } from '../../features/surveys/builder/PageCanvas';
import { FieldSettings } from '../../features/surveys/builder/FieldSettings';
import { PageSettings } from '../../features/surveys/builder/PageSettings';
import { SurveySettings } from '../../features/surveys/builder/SurveySettings';
import { PublishModal } from '../../features/surveys/builder/PublishModal';
import { CopyLinkButton } from '../../features/surveys/CopyLinkButton';
import { Btn, Icon, ICONS, StatusChip } from '../../features/forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../features/forms/ui/tokens';

const AUTOSAVE_DEBOUNCE_MS = 800;

type SettingsTab = 'survey' | 'page' | 'field';

export function SurveyBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useSurvey(id);
  const state = useSurveyBuilderStore();
  const saveDraft = useSaveSurveyDraft(id ?? '');
  const updateSurvey = useUpdateSurvey(id ?? '');
  const publish = usePublishSurvey(id ?? '');

  const [savingState, setSavingState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('survey');
  const [publishOpen, setPublishOpen] = useState(false);

  // Hydrate the store once when the survey loads.
  useEffect(() => {
    if (!data) return;
    state.initFrom(data.survey, data.current_version?.pages ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.survey.id]);

  // Flip to the Field tab whenever a field becomes selected.
  useEffect(() => {
    if (state.selectedFieldKey) setSettingsTab('field');
  }, [state.selectedFieldKey]);

  const flush = async () => {
    if (!id) return;
    setSavingState('saving');
    try {
      await Promise.all([
        updateSurvey.mutateAsync({
          name: state.name,
          slug: state.slug,
          audience_mode: state.audienceMode,
          response_limit: state.responseLimit,
          dedupe_key: state.dedupeKey,
          brand_override: state.brandOverride,
          consent_mode: state.consentMode,
          consent_text: state.consentText,
          reminder_offsets: state.reminderOffsets,
          max_reminders: state.maxReminders,
        }),
        saveDraft.mutateAsync({ pages: state.pages }),
      ]);
      state.markSaved();
      setSavedAt(new Date());
      setSavingState('saved');
    } catch {
      setSavingState('idle');
    }
  };

  // Debounced auto-save on any dirty change.
  useEffect(() => {
    if (!state.dirty || !id) return;
    const timer = setTimeout(() => {
      void flush();
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.dirty,
    state.pages,
    state.name,
    state.slug,
    state.audienceMode,
    state.responseLimit,
    state.dedupeKey,
    state.brandOverride,
    state.consentMode,
    state.consentText,
    state.reminderOffsets,
    state.maxReminders,
  ]);

  if (isLoading || !data) {
    return <div style={{ padding: 40, fontFamily: FORM_UI_FONT, color: C.sub, textAlign: 'center' }}>Loading…</div>;
  }

  const selectedPage = state.pages.find((p) => p.id === state.selectedPageId) ?? null;
  const selectedPageNumber = state.pages.findIndex((p) => p.id === state.selectedPageId) + 1;
  const selectedField = selectedPage?.fields.find((f) => f.key === state.selectedFieldKey) ?? null;
  const readinessIssues = checkSurveyReadiness(state);

  const openPublish = async () => {
    await flush(); // ensure the draft the validator reads is current
    setPublishOpen(true);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 53px)',
        fontFamily: FORM_UI_FONT,
        color: C.ink,
        background: C.bg,
      }}
    >
      {/* TOP BAR */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 24px', background: '#fff', borderBottom: `1px solid ${C.line}` }}>
        <button
          type="button"
          onClick={() => navigate('/surveys')}
          style={{ fontSize: 12, color: C.faint, fontWeight: 500, background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: FORM_UI_FONT, padding: 0 }}
        >
          Engage › Surveys ›
        </button>
        <input
          value={state.name}
          onChange={(e) => state.setName(e.target.value)}
          style={{
            fontFamily: FORM_UI_FONT,
            fontWeight: 700,
            fontSize: 17,
            color: C.indigo,
            border: '1px solid transparent',
            borderRadius: 7,
            padding: '4px 8px',
            width: 320,
            background: 'transparent',
            outline: 'none',
          }}
        />
        <StatusChip status={data.survey.status} />
        <SaveBadge state={savingState} savedAt={savedAt} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          {data.survey.current_published_version_id && state.audienceMode === 'ANONYMOUS' && (
            <CopyLinkButton slug={state.slug} />
          )}
          <Btn kind="ghost" onClick={() => navigate(`/surveys/${data.survey.id}/results`)}>
            <Icon d={ICONS.layers} size={15} color={C.ink} />
            Results
          </Btn>
          <Btn kind="primary" onClick={openPublish}>
            Publish
          </Btn>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* LEFT — pages + palette */}
        <aside style={{ width: 248, background: '#fff', borderRight: `1px solid ${C.line}`, overflowY: 'auto' }}>
          <PageRail
            pages={state.pages}
            selectedPageId={state.selectedPageId}
            onSelect={(pid) => {
              state.selectPage(pid);
              setSettingsTab('page');
            }}
            onAdd={() => {
              state.addPage();
              setSettingsTab('page');
            }}
            onMove={state.movePage}
            onRemove={state.removePage}
          />
          <FieldPalette
            pageSelected={!!state.selectedPageId}
            existingKeys={state.pages.flatMap((p) => p.fields.map((f) => f.key))}
            onAdd={(type) => state.selectedPageId && state.addField(state.selectedPageId, type)}
            onAddPreset={(preset) => state.selectedPageId && state.addPresetField(state.selectedPageId, preset)}
          />
        </aside>

        {/* CENTRE — canvas */}
        <main style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '20px 26px' }}>
          <PageCanvas
            page={selectedPage}
            pageNumber={selectedPageNumber}
            selectedFieldKey={state.selectedFieldKey}
            onSelectField={(key) => state.selectField(key)}
            onMoveField={(key, dir) => selectedPage && state.moveField(selectedPage.id, key, dir)}
            onRemoveField={(key) => selectedPage && state.removeField(selectedPage.id, key)}
          />
        </main>

        {/* RIGHT — settings */}
        <aside style={{ width: 348, background: '#fff', borderLeft: `1px solid ${C.line}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', borderBottom: `1px solid ${C.line}` }}>
            <TabButton label="Survey" active={settingsTab === 'survey'} onClick={() => setSettingsTab('survey')} />
            <TabButton label="Page" active={settingsTab === 'page'} disabled={!state.selectedPageId} onClick={() => state.selectedPageId && setSettingsTab('page')} />
            <TabButton label="Field" active={settingsTab === 'field'} disabled={!selectedField} onClick={() => selectedField && setSettingsTab('field')} />
          </div>
          <div style={{ overflowY: 'auto', padding: '18px 18px 30px', flex: 1 }}>
            {settingsTab === 'field' && selectedField && selectedPage ? (
              <FieldSettings
                field={selectedField}
                onChange={(patch) => state.updateField(selectedPage.id, selectedField.key, patch)}
                onRemove={() => {
                  state.removeField(selectedPage.id, selectedField.key);
                  setSettingsTab('page');
                }}
                onClose={() => {
                  state.selectField(null);
                  setSettingsTab('page');
                }}
              />
            ) : settingsTab === 'page' ? (
              <PageSettings />
            ) : (
              <SurveySettings />
            )}
          </div>
        </aside>
      </div>

      {publishOpen && (
        <PublishModal
          surveyId={data.survey.id}
          slug={state.slug}
          audienceMode={state.audienceMode}
          nextVersionNumber={(data.published_version?.version_number ?? 0) + 1}
          isPublishing={publish.isPending}
          onClose={() => setPublishOpen(false)}
          onPublish={async () => {
            await publish.mutateAsync();
          }}
        />
      )}

      {readinessIssues.length > 0 && (
        <div style={{ background: C.warnBg, color: C.warn, fontSize: 11.5, fontWeight: 600, textAlign: 'center', padding: '7px 0' }}>
          {readinessIssues.length} thing{readinessIssues.length === 1 ? '' : 's'} to fix before publishing. Open Publish for details.
        </div>
      )}
    </div>
  );
}

function SaveBadge(props: { state: 'idle' | 'saving' | 'saved'; savedAt: Date | null }) {
  if (props.state === 'idle') return null;
  const label =
    props.state === 'saving'
      ? 'Saving…'
      : props.savedAt
        ? `Saved ${props.savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} · auto-saving`
        : 'Saved · auto-saving';
  return (
    <span style={{ fontSize: 11.5, color: C.faint, display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 6, height: 6, borderRadius: 99, background: props.state === 'saving' ? C.warnDot : C.teal }} />
      {label}
    </span>
  );
}

function TabButton(props: { label: string; active: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={props.disabled ? undefined : props.onClick}
      disabled={props.disabled}
      style={{
        flex: 1,
        fontFamily: FORM_UI_FONT,
        fontWeight: 600,
        fontSize: 13,
        cursor: props.disabled ? 'default' : 'pointer',
        padding: '13px 0',
        border: 'none',
        background: 'transparent',
        color: props.active ? C.indigo : props.disabled ? C.faint : C.sub,
        borderBottom: `2px solid ${props.active ? C.indigo : 'transparent'}`,
      }}
    >
      {props.label}
    </button>
  );
}
